import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCategories_search_edges_node } from "@saleor/searches/types/SearchCategories";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";
export interface FormData {
  category: SearchCategories_search_edges_node[];
  query: string;
  onBrandChange(event: React.ChangeEvent<any>);
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignCategoryDialog" }
);

export interface AssignCategoryDialogProps extends FetchMoreProps {
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  categories: SearchCategories_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SearchCategories_search_edges_node[]) => void;
  onSelectLevel: (value: string) => void;
}

function handleCategoryAssign(
  category: SearchCategories_search_edges_node,
  isSelected: boolean,
  selectedCategories: SearchCategories_search_edges_node[],
  setSelectedCategories: (data: SearchCategories_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedCategories(
      selectedCategories.filter(
        selectedCategory => selectedCategory.id !== category.id
      )
    );
  } else {
    setSelectedCategories([category]);
  }
}

const AssignCategoryDialog: React.FC<AssignCategoryDialogProps> = props => {
  const {
    categories,
    confirmButtonState,
    hasMore,
    open,
    loading,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit,
    onSelectLevel
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedCategories, setSelectedCategories] = React.useState<
    SearchCategories_search_edges_node[]
  >([]);
  const container = React.useRef<HTMLDivElement>();
  const handleSubmit = () => onSubmit(selectedCategories);
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: ""
  };
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");

  const sellerLevelChoices = maybe(
    () =>
      [0, 1, 2, 3, 4, 5].map(level => ({
        label: `${level}`,
        value: level
      })),
    []
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerLevelChoices
        );

        let btnDisabled = false;
        if (formData.sellerBrand === "") {
          btnDisabled = true;
        } else if (
          formData.sellerBrand !== "" &&
          categories &&
          categories.length
        ) {
          btnDisabled = false;
        } else if (
          formData.sellerBrand === "" &&
          categories &&
          categories.length
        ) {
          btnDisabled = false;
        } else {
          btnDisabled = true;
        }
        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableDialogClasses.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Category"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <SingleAutocompleteSelectField
                label={intl.formatMessage({
                  defaultMessage: "Category Level"
                })}
                name="sellerBrand"
                displayValue={sellerDisplayName}
                choices={sellerLevelChoices}
                disabled={false}
                value={formData.sellerBrand}
                onChange={e => {
                  handleSelectBrand(e);
                  onSelectLevel(e.target.value);
                }}
              />
              <FormSpacer />
              <TextField
                name="query"
                value={query}
                disabled={formData.sellerBrand === "" ? true : false}
                onChange={onQueryChange}
                label={intl.formatMessage({
                  defaultMessage: "Search Category"
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Search by category name"
                })}
                fullWidth
                InputProps={{
                  autoComplete: "off",
                  endAdornment: loading && <CircularProgress size={16} />
                }}
              />
              {formData.sellerBrand !== "" ? (
                <>
                  <FormSpacer />
                  <div
                    className={scrollableDialogClasses.scrollArea}
                    style={{ height: "70%" }}
                  >
                    <InfiniteScroll
                      pageStart={0}
                      loadMore={onFetchMore}
                      hasMore={hasMore}
                      useWindow={false}
                      loader={
                        <div
                          className={
                            scrollableDialogClasses.loadMoreLoaderContainer
                          }
                          key={0}
                        >
                          <CircularProgress size={16} />
                        </div>
                      }
                      threshold={10}
                    >
                      <ResponsiveTable key="table">
                        <TableBody>
                          {categories &&
                            categories.map(category => {
                              const isSelected = selectedCategories.some(
                                selectedCategory =>
                                  selectedCategory.id === category.id
                              );

                              return (
                                <TableRow key={category.id}>
                                  <TableCell className={classes.colName}>
                                    {category.name}
                                  </TableCell>
                                  <TableCell
                                    padding="checkbox"
                                    className={classes.checkboxCell}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() =>
                                        handleCategoryAssign(
                                          category,
                                          isSelected,
                                          selectedCategories,
                                          setSelectedCategories
                                        )
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </ResponsiveTable>
                    </InfiniteScroll>
                    {categories && categories.length
                      ? ""
                      : "No Categories Found"}
                  </div>
                </>
              ) : (
                ""
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage {...buttonMessages.back} />
              </Button>
              <ConfirmButton
                transitionState={confirmButtonState}
                color="primary"
                variant="contained"
                type="submit"
                onClick={submit}
                disabled={btnDisabled || selectedCategories.length === 0}
              >
                <FormattedMessage
                  defaultMessage="Assign category"
                  description="button"
                />
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignCategoryDialog.displayName = "AssignCategoryDialog";
export default AssignCategoryDialog;
